import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useSorSearch, type SorOrganisation } from '@/hooks/useSorLookup';
import type { GgopPartyDraft } from '@/lib/ggop/ggopForm';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE, TYPE, UI_MUTED } from '@/lib/typography';
import { cn } from '@/utils/cn';

// A GGOP party (receiver, copy receiver, control location) from SOR: EAN (lokationsnummer),
// organisation identifier and its IdentifierCode (NSK 1.0.4: kommunenummer, ydernummer,
// sygehusafdelingsnummer, …). SOR gives the EAN and, for hospitals and practices, the
// identifier; a municipality's kommunenummer is typed in.

const IDENTIFIER_LABELS: Record<string, string> = {
  kommunenummer: 'Kommunenummer',
  ydernummer: 'Ydernummer',
  sygehusafdelingsnummer: 'Sygehusafdelingsnummer',
  lokationsnummer: 'Lokationsnummer',
  region: 'Regionsnummer',
  sorkode: 'SOR-kode',
};

export function partyFromSor(org: SorOrganisation): GgopPartyDraft {
  return {
    ean: org.ean,
    identifier: org.identifier ?? '',
    identifierCode: org.identifierCode ?? '',
    organisationName: org.name,
    streetName: org.address.street ?? undefined,
    postCodeIdentifier: org.address.postCode ?? undefined,
    districtName: org.address.district ?? undefined,
  };
}

const selectClass =
  'mt-1 block w-full rounded-md border border-ds-field px-3 py-2 shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal   ';

export function GgopPartyPicker({
  fieldId,
  label,
  value,
  onChange,
  identifierCodes,
  removeLabel,
}: {
  fieldId: string;
  label: string;
  value: GgopPartyDraft | null;
  onChange: (value: GgopPartyDraft | null) => void;
  identifierCodes: readonly string[];
  removeLabel?: string;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const search = useSorSearch(value ? '' : query, null);

  if (value) {
    const address = [value.streetName, [value.postCodeIdentifier, value.districtName].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    return (
      <div className="space-y-3 rounded-md border border-ds-line p-3" data-testid={`${fieldId}-picked`}>
        <div>
          <div className={FORM_LABEL_TYPE}>{label}</div>
          <p className={cn('mt-1', TYPE.body, 'text-ds-text ')}>{value.organisationName}</p>
          <p className={UI_MUTED}>{[`EAN ${value.ean}`, address].filter(Boolean).join(' · ')}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className={FORM_LABEL_TYPE}>{t('patients.ggop.identifier_code', 'Identifikatorkode')}</span>
            <select
              id={`${fieldId}-code`}
              className={cn(TYPE.inputValue, selectClass, !value.identifierCode && 'border-ds-red')}
              value={value.identifierCode}
              onChange={(event) => onChange({ ...value, identifierCode: event.target.value })}
            >
              <option value="">{t('patients.ggop.identifier_code_choose', 'Vælg identifikatorkode')}</option>
              {identifierCodes.map((code) => (
                <option key={code} value={code}>
                  {IDENTIFIER_LABELS[code] ?? code}
                </option>
              ))}
            </select>
          </label>
          <Input
            id={`${fieldId}-identifier`}
            label={t('patients.ggop.identifier', 'Identifikator')}
            value={value.identifier}
            onChange={(event) => onChange({ ...value, identifier: event.target.value })}
            error={value.identifier.trim() ? undefined : t('patients.ggop.identifier_missing', 'Identifikatoren mangler.')}
          />
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
          {removeLabel ?? t('patients.ggop.party_change', 'Vælg en anden')}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <Input
        id={fieldId}
        label={label}
        value={query}
        autoComplete="off"
        placeholder={t('patients.ggop.sor_search', 'Søg i SOR (navn, EAN eller SOR-id)')}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.preventDefault();
        }}
      />
      {search.isLoading && <p className={cn('mt-2', FORM_HINT_TYPE)}>{t('patients.ggop.searching', 'Søger…')}</p>}
      {search.error ? <p className={cn('mt-2', FORM_ERROR_TYPE)}>{t('patients.ggop.search_failed', 'Søgningen fejlede.')}</p> : null}
      {search.data && search.data.length === 0 && <p className={cn('mt-2', FORM_HINT_TYPE)}>{t('patients.ggop.no_results', 'Ingen resultater')}</p>}
      {search.data && search.data.length > 0 && (
        <ul className="mt-2 max-h-60 space-y-1 overflow-auto">
          {search.data.map((org) => (
            <li key={org.sorId}>
              <button
                type="button"
                className="w-full rounded-md border border-ds-line px-3 py-2 text-left hover:bg-ds-row-hover"
                onClick={() => onChange(partyFromSor(org))}
              >
                <span className={cn('block', TYPE.body, 'text-ds-text ')}>{org.name}</span>
                <span className={cn('block', UI_MUTED)}>
                  {[org.ean, org.address.postCode, org.address.district, org.ediReceiveTypes.some((type) => /GGOP/i.test(type)) ? 'GGOP' : null]
                    .filter((part): part is string => Boolean(part))
                    .join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
